'use client'

import posthog from 'posthog-js'

type AnalyticsProperty = string | number | boolean | null | undefined

export type AnalyticsEvent =
  | 'booking_intent_clicked'
  | 'contact_clicked'
  | 'destination_selected'
  | 'experience_selected'
  | 'experience_viewed'
  | 'inquiry_opened'
  | 'inquiry_submission_failed'
  | 'inquiry_submission_succeeded'
  | 'inquiry_submitted'
  | 'newsletter_subscription_failed'
  | 'newsletter_subscription_succeeded'
  | 'newsletter_subscription_submitted'
  | 'partner_catalogue_downloaded'
  | 'partner_interest_started'
  | 'partner_interest_viewed'
  | 'pre_book_closed'
  | 'pre_book_field_completed'
  | 'pre_book_opened'
  | 'pre_book_session_selected'
  | 'pre_book_step_continued'
  | 'pre_book_submitted'
  | 'weekend_trip_viewed'
  | 'weekend_trip_date_selected'
  | 'weekend_trip_booking_submitted'
  | 'weekend_trip_booking_succeeded'
  | 'weekend_trip_booking_failed'
  | 'weekend_trip_app_cta_clicked'
  | 'social_link_clicked'
  | 'site_button_clicked'
  | 'site_navigation_clicked'
  | 'theme_selected'

export function captureAnalyticsEvent(
  event: AnalyticsEvent,
  properties: Record<string, AnalyticsProperty> = {},
) {
  if (typeof window === 'undefined' || !posthog.__loaded) return

  posthog.capture(event, properties)
}

function safeLinkLabel(link: HTMLAnchorElement) {
  return (link.getAttribute('aria-label') || link.textContent || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100)
}

/** Captures high-value outbound interactions without storing URL query values. */
export function captureTrackedLink(link: HTMLAnchorElement) {
  const href = link.getAttribute('href')
  if (!href) return

  const commonProperties = {
    link_label: safeLinkLabel(link),
    source_path: window.location.pathname,
  }

  if (href.startsWith('mailto:')) {
    captureAnalyticsEvent('contact_clicked', {
      ...commonProperties,
      channel: 'email',
    })
    return
  }

  if (href.startsWith('tel:')) {
    captureAnalyticsEvent('contact_clicked', {
      ...commonProperties,
      channel: 'phone',
    })
    return
  }

  let url: URL
  try {
    url = new URL(href, window.location.origin)
  } catch {
    return
  }

  if (link.dataset.analyticsEvent === 'experience_selected') {
    captureAnalyticsEvent('experience_selected', {
      ...commonProperties,
      destination_path: url.pathname,
      destination: link.dataset.analyticsDestination,
      experience_slug: link.dataset.analyticsExperienceSlug,
      experience_name: link.dataset.analyticsExperienceName,
      selection_source: link.dataset.analyticsSelectionSource,
      theme: link.dataset.analyticsTheme,
    })
  }

  if (url.hostname === 'wa.me' || url.hostname.endsWith('.whatsapp.com')) {
    captureAnalyticsEvent('booking_intent_clicked', {
      ...commonProperties,
      channel: 'whatsapp',
      experience_slug: link.dataset.analyticsExperienceSlug,
    })
    return
  }

  if (url.hostname === 'app.oorva.in') {
    captureAnalyticsEvent('booking_intent_clicked', {
      ...commonProperties,
      channel: 'booking_app',
      destination_path: url.pathname,
      experience_slug: link.dataset.analyticsExperienceSlug,
    })
    return
  }

  if (url.hostname.includes('instagram.com') || url.hostname.includes('linkedin.com')) {
    captureAnalyticsEvent('social_link_clicked', {
      ...commonProperties,
      platform: url.hostname.includes('instagram.com') ? 'instagram' : 'linkedin',
    })
    return
  }

  if (url.origin === window.location.origin) {
    captureAnalyticsEvent('site_navigation_clicked', {
      ...commonProperties,
      destination_path: url.pathname,
      destination_section: url.hash || undefined,
    })
  }
}

export function captureTrackedButton(button: HTMLButtonElement) {
  captureAnalyticsEvent('site_button_clicked', {
    button_label: (button.getAttribute('aria-label') || button.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 100),
    button_type: button.type,
    source_path: window.location.pathname,
  })
}
